import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Shield, Mail, Lock, ArrowRight, Fingerprint } from 'lucide-react';
import {
  isPlatformPasskeyAvailable,
  emailHasPasskey,
  loginWithPasskey,
  getRememberedPasskeyEmail,
  rememberPasskeyEmail,
} from '../lib/webauthn';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passkeyReady, setPasskeyReady] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const { login, loginWithPasskeyToken, getRememberedCredentials } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const saved = getRememberedCredentials();
    const hint = getRememberedPasskeyEmail();
    if (saved) {
      setEmail(saved.email || '');
      setPassword(saved.password || '');
      setRememberMe(true);
    } else if (hint) {
      setEmail(hint);
    }
  }, [getRememberedCredentials]);

  // When email + platform authenticator are both present, check if that email has a passkey
  useEffect(() => {
    let cancelled = false;
    async function check() {
      const trimmed = email.trim().toLowerCase();
      if (!trimmed || !/^[^@]+@[^@]+\.[^@]+$/.test(trimmed)) {
        if (!cancelled) setPasskeyReady(false);
        return;
      }
      const platform = await isPlatformPasskeyAvailable();
      if (!platform) {
        if (!cancelled) setPasskeyReady(false);
        return;
      }
      const has = await emailHasPasskey(trimmed);
      if (!cancelled) setPasskeyReady(has);
    }
    const t = setTimeout(check, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password, rememberMe);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handlePasskeyLogin = async () => {
    setError('');
    setPasskeyBusy(true);
    try {
      const data = await loginWithPasskey(email.trim().toLowerCase());
      loginWithPasskeyToken(data);
      rememberPasskeyEmail(email.trim().toLowerCase());
      navigate('/dashboard');
    } catch (err) {
      // Silent fallback: leave the password field for the user
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'AbortError') {
        // User cancelled — do not surface an error
      } else {
        setError('Biometric sign-in was not available. Please use your password.');
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#36454F]">
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
          <Shield className="w-14 h-14 mb-8 text-[#E0BFB8]" strokeWidth={1.5} />
          <h1 className="font-['Playfair_Display'] text-4xl font-semibold mb-6 text-center tracking-tight">
            Selah Slate&trade;
          </h1>
          <div className="max-w-sm text-center">
            <p className="text-base text-white/80 font-['Manrope'] leading-relaxed mb-8">
              Your past doesn't define your legacy. Financial "curses" end here. We've built a path to help you clear the record and start fresh.
            </p>
            <div className="space-y-3 text-left mb-8">
              <div className="flex items-center space-x-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#E0BFB8] flex-shrink-0"></div>
                <span className="text-sm text-white/70 font-['Manrope']">Stop the cycle of errors.</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#E0BFB8] flex-shrink-0"></div>
                <span className="text-sm text-white/70 font-['Manrope']">Restore your financial identity.</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#E0BFB8] flex-shrink-0"></div>
                <span className="text-sm text-white/70 font-['Manrope']">Rebuild a foundation that lasts.</span>
              </div>
            </div>
            <div className="border-t border-white/10 pt-6">
              <p className="font-['Playfair_Display'] text-sm italic text-[#E0BFB8] leading-relaxed">
                "I am doing a new thing; now it springs forth, do you not perceive it?"
              </p>
              <p className="text-xs text-white/40 font-['Manrope'] mt-2">— Isaiah 43:19</p>
            </div>

            {/* Is / Is not */}
            <div className="border-t border-white/10 pt-6 space-y-2.5" data-testid="login-is-isnt">
              <p className="text-[10px] uppercase tracking-wider text-[#E0BFB8] font-['Manrope'] font-semibold">
                A word about what we are
              </p>
              <p className="text-xs text-white/75 font-['Manrope'] leading-relaxed">
                Selah Slate is an <strong className="text-white">all-in-one, free, faith-based companion</strong> that
                helps you analyze your credit report, draft FCRA dispute letters, track bureau
                responses, sync a shared household budget in real time, guide CFPB escalations, and
                plan debt payoff — grounded in scripture.
              </p>
              <p className="text-[10px] uppercase tracking-wider text-[#E0BFB8] font-['Manrope'] font-semibold pt-1">
                What we are not — and what we don't do
              </p>
              <p className="text-xs text-white/60 font-['Manrope'] leading-relaxed">
                We are <em>not</em> a credit-repair company, law firm, or financial advisor.
                We don't mail letters for you or contact bureaus on your behalf — you stay
                in control. We never sell your data or show ads.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F5F5F5]">
        <div className="w-full max-w-md animate-fade-in-up">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <Shield className="w-10 h-10 text-[#B76E79] mr-3" strokeWidth={1.5} />
            <span className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F]">
              Selah Slate&trade;
            </span>
          </div>

          <div className="bg-white p-8 border border-[#36454F]/15">
            <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-[#36454F] mb-2">
              Welcome back
            </h2>
            <p className="text-[#36454F]/65 mb-8 font-['Manrope']">
              Sign in to continue your credit dispute journey
            </p>

            {error && (
              <div className="mb-6 p-4 bg-[#D9534F]/10 border border-[#D9534F]/30 text-[#D9534F] text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#36454F] font-['Manrope'] font-medium">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#36454F]/40" strokeWidth={1.5} />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-10 h-12 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope']"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#36454F] font-['Manrope'] font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#36454F]/40" strokeWidth={1.5} />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className={`pl-10 ${passkeyReady ? 'pr-12' : 'pr-3'} h-12 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope']`}
                    required
                    data-testid="login-password-input"
                  />
                  {passkeyReady && (
                    <button
                      type="button"
                      onClick={handlePasskeyLogin}
                      disabled={passkeyBusy}
                      title="Sign in with Face ID / Touch ID / Windows Hello"
                      aria-label="Sign in with biometrics"
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-sm text-[#B76E79] hover:bg-[#B76E79]/10 disabled:opacity-40 transition-colors"
                      data-testid="passkey-login-icon-btn"
                    >
                      {passkeyBusy ? (
                        <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                      ) : (
                        <Fingerprint className="w-5 h-5" strokeWidth={1.5} />
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Remember Me */}
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <Checkbox
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                    className="border-[#36454F]/30 data-[state=checked]:bg-[#B76E79] data-[state=checked]:border-[#B76E79]"
                    data-testid="remember-me-checkbox"
                  />
                  <span className="text-sm text-[#36454F]/70 font-['Manrope']">Remember me</span>
                </label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-[#B76E79] hover:text-[#9e5c66] font-['Manrope'] transition-colors"
                  data-testid="forgot-password-link-inline"
                >
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] font-medium rounded-sm transition-all duration-300"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    Sign In
                    <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
                  </span>
                )}
              </Button>
            </form>

            <p className="mt-8 text-center text-[#36454F]/65 font-['Manrope']">
              Don't have an account?{' '}
              <Link 
                to="/register" 
                className="text-[#B76E79] hover:text-[#9e5c66] font-medium transition-colors"
                data-testid="register-link"
              >
                Create one free
              </Link>
            </p>
          </div>

          <p className="mt-6 text-center text-[10px] text-[#36454F]/40 font-['Manrope'] max-w-md mx-auto leading-relaxed">
            Selah Slate is a financial wellness initiative of Selah and Stone Legacy Holdings LLC. All services are provided free of charge as part of our community legacy mission.
          </p>
        </div>
      </div>
    </div>
  );
}
